/**
 * One rule set for stripping the credentials this app can be handed in a URL, shared by
 * every analytics SDK that ships a location off the device (PostHog's `before_send`,
 * Sentry's event processor and replay recording hook).
 *
 * Auth redirects hand the app credentials in three places: the OAuth implicit flow returns
 * `#access_token=…&refresh_token=…&provider_token=…` to /auth/callback (recovery links do the
 * same for /reset-password); the PKCE branch arrives as `?token_hash=…&type=recovery` on
 * /auth/confirm, which is replayable into `verifyOtp` until it is spent; and
 * /invite/accept?token=… / /share/:token carry bearer tokens in the query and the path.
 * supabase-js does strip the hash, but asynchronously inside `detectSessionInUrl` while the
 * client initialises, so anything that reads `location.href` during app start can win that
 * race — and nothing strips the query at all.
 */

/**
 * Query keys our links carry a live credential in: the PKCE recovery / confirmation
 * `token_hash` (/auth/confirm), the invite `token` (/invite/accept), the same invite
 * bearer under the name it is handed on to signup with (`invite_token` — InviteAccept
 * sends a signed-out invitee to /signup?invite_token=…, where SignUp reads it back),
 * and the OAuth values in case a provider ever returns them in the query rather than
 * the hash.
 */
const CREDENTIAL_PARAMS = [
  "token_hash",
  "token",
  "invite_token",
  "code",
  "access_token",
  "refresh_token",
  "oauth_query",
  "sig",
  "state",
  "code_challenge",
];

/** Routes where the next path segment IS the credential — /share/:token is a bearer link. */
const CREDENTIAL_PATH_PREFIXES = ["/share/"];

/**
 * Params whose value is itself a location, so a credential hides one level down where
 * `searchParams.has()` cannot see it. Every post-auth destination we build is encoded
 * into `next`: `/auth/callback?next=%2Finvite%2Faccept%3Ftoken%3Dinv_abc` carries a live
 * invite token with no top-level `token` param at all (GoogleSignInButton, SignUp,
 * Login), and `/auth/confirm?next=…` nests the same beside the `token_hash` we do catch.
 */
const NESTED_LOCATION_PARAMS = ["next", "redirect_to"];

/** Origin lent to a relative nested value so it can be parsed; sliced back off after. */
const NESTED_BASE = "http://nested.invalid";

/** Our links nest one deep; the cap only stops a hand-crafted value from spinning. */
const MAX_NESTING_DEPTH = 3;

const REDACTED = "redacted";

export function redactPath(pathname: string): string {
  const prefix = CREDENTIAL_PATH_PREFIXES.find((candidate) => pathname.startsWith(candidate));
  return prefix ? prefix + REDACTED : pathname;
}

/**
 * Redact a value that is relative, keeping it as relative as it arrived: lend it the part of an
 * absolute URL it is missing, redact, then take that part back off. A breadcrumb must never
 * name an origin the app never visited.
 *
 * The two shapes need different loans. A path takes the whole sentinel origin. A network-path
 * reference (`//host/share/tok`) names its own host, so concatenating the sentinel would leave
 * that authority sitting in the PATH — `http://nested.invalid//host/share/tok`, which no rule
 * matches and whose credential would ship intact; it takes a scheme alone.
 */
function redactRelative(value: string, depth: number): string {
  const [loan, absolute] = value.startsWith("//")
    ? ["http:", `http:${value}`]
    : [NESTED_BASE, NESTED_BASE + value];
  const redacted = redactUrl(absolute, depth + 1);
  return redacted.startsWith(loan) ? redacted.slice(loan.length) : redacted;
}

/**
 * Redact a location that may be absolute or relative — a `next` param, a navigation
 * breadcrumb's `from`/`to`, a replay history frame. A value that is neither a path nor an
 * http(s) URL is not a location and is returned untouched.
 */
export function redactLocation(value: string, depth = 0): string {
  if (depth >= MAX_NESTING_DEPTH) return value;
  if (value.startsWith("/")) return redactRelative(value, depth);
  // Read the scheme with the URL parser rather than a pattern: this runs on attacker-supplied
  // param values, and the parser cannot backtrack (it also accepts an upper-case scheme).
  let protocol: string;
  try {
    protocol = new URL(value).protocol;
  } catch {
    return value;
  }
  return protocol === "http:" || protocol === "https:" ? redactUrl(value, depth + 1) : value;
}

/**
 * Does this location still carry one of the credentials above, in its fragment, query or path?
 *
 * For the one carrier that cannot be redacted on the way out: rrweb writes the page URL into
 * its Meta frame and Sentry's replay recording exposes no hook over it, so the recorder has to
 * WAIT for a clean URL instead (see `lib/sentry/replayGate`).
 *
 * Any non-empty fragment counts. It is where the implicit flow returns `access_token` /
 * `refresh_token` / `provider_token`, `redactUrl` drops fragments wholesale rather than naming
 * their keys, and this app links to no `#anchor` — so "there is a fragment" is a sound proxy
 * for "there may be a credential in it", and errs towards recording less.
 */
export function hasUrlCredential(value: string): boolean {
  const hashIndex = value.indexOf("#");
  if (hashIndex !== -1 && hashIndex < value.length - 1) return true;

  const withoutFragment = hashIndex === -1 ? value : value.slice(0, hashIndex);
  return redactLocation(withoutFragment) !== withoutFragment;
}

export function redactUrl(value: string, depth = 0): string {
  const withoutFragment = value.includes("#") ? value.slice(0, value.indexOf("#")) : value;

  let url: URL;
  try {
    url = new URL(withoutFragment);
  } catch {
    // "$direct" and other non-URLs: dropping the fragment is all that can be done.
    return withoutFragment;
  }

  const redactedPath = redactPath(url.pathname);
  const credentialKeys = CREDENTIAL_PARAMS.filter((key) => url.searchParams.has(key));
  const nestedRedactions = NESTED_LOCATION_PARAMS.flatMap((key) => {
    const nested = url.searchParams.get(key);
    if (nested === null) return [];
    const redacted = redactLocation(nested, depth);
    return redacted === nested ? [] : [[key, redacted] as const];
  });
  // Rebuild only when something is actually redacted, so ordinary URLs are reported
  // byte-identical rather than URL-normalised.
  if (
    redactedPath === url.pathname &&
    credentialKeys.length === 0 &&
    nestedRedactions.length === 0
  ) {
    return withoutFragment;
  }

  url.pathname = redactedPath;
  for (const key of credentialKeys) url.searchParams.set(key, REDACTED);
  for (const [key, redacted] of nestedRedactions) url.searchParams.set(key, redacted);
  return url.toString();
}
